import React, { memo, useCallback, useMemo, useState } from 'react'
import ApolloErrorMessages from 'transfix-ui/components/ApolloErrorMessages'
import {
  ConfirmationModal,
  IConfirmationModalProps,
} from 'transfix-ui/components/Modals/ConfirmationModal'
import { IModalAction } from 'transfix-ui/components/Modals/ModalAction'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { IError } from 'types/graphqlTypes'
import { useMPAnalytics } from 'apps/marketplace/hooks/useMPAnalytics'
import { useDeclineOptionMutation } from './graphql/DeclineOptionMutation'

interface IDeclineOptionConfirmationModalProps extends Pick<IConfirmationModalProps, 'onClose'> {
  shipmentId: string
  optionId: string
  onSuccess: () => void
}

const DeclineOptionConfirmationModal = memo<IDeclineOptionConfirmationModalProps>(
  ({ onClose, onSuccess, optionId, shipmentId }) => {
    const { trackEvent } = useMPAnalytics()
    const [mutationErrors, setMutationErrors] = useState<IError[]>([])

    const { openToast } = useGlobalToast()
    const [declineOptionMutation, { error, loading }] = useDeclineOptionMutation()

    const declineBidHandler = useCallback(async () => {
      setMutationErrors([])
      const { data } = await declineOptionMutation({
        variables: {
          input: {
            id: optionId,
          },
        },
      })
      const { declineBid } = data || {}
      const { bid, errors = [] } = declineBid || {}
      const { id: bidId } = bid || {}

      if (errors.length) {
        trackEvent('Carrier Options', 'ERROR_DECLINING_OPTION', {
          carrierOptionId: optionId,
          errors,
        })

        setMutationErrors(errors)
        return
      }

      if (bidId) {
        trackEvent('Carrier Options', 'DECLINED_OPTION', { carrierOptionId: bidId })
        openToast({
          toastMessage: 'Option Declined',
          toastType: 'default',
          toastCta: {
            label: `Shipment #${shipmentId}`,
            url: `/shipment-details/${shipmentId}`,
            target: '_blank',
          },
        })
        onSuccess()
      }
    }, [declineOptionMutation, onSuccess, openToast, optionId, shipmentId, trackEvent])

    const modalActions: IModalAction[] = useMemo(
      () => [
        { label: 'Cancel', type: 'dismiss', disabled: loading, action: onClose },
        { label: 'Decline Option', type: 'confirm', disabled: loading, action: declineBidHandler },
      ],
      [declineBidHandler, loading, onClose]
    )

    return (
      <ConfirmationModal
        actions={modalActions}
        onClose={onClose}
        title='Are you sure that you want to decline this option?'
        titleSize={4}
        open>
        <ApolloErrorMessages mutationErrors={mutationErrors} apolloErrors={error} />
      </ConfirmationModal>
    )
  }
)

export default DeclineOptionConfirmationModal
